import type {
  SupportedChain,
  FiatPriceResponse,
  MarketListResponse
} from "@/types/funds.types";
import { authApi } from "./api.service";
import type {
  AllCurrenciesResponse, AllSymbolsResponse, AllTickersResponse,
  CurrencyDetails, ServerTimeResponse, SymbolDetails
} from "../types/symbol.types";

// ================= Supported Chains ================= //

export const getSupportedChains = async (): Promise<SupportedChain[]> => {
  const response = await authApi.get<SupportedChain[]>('/v0/kucoin/exchange/chains/supported');
  return response.data;
};

// ================= Fiat Price ================= //

export const getFiatPrice = async (
  currencies?: string,
  base?: string
): Promise<FiatPriceResponse> => {
  const params = new URLSearchParams();

  if (currencies) {
    params.append('currencies', currencies);
  }

  if (base) {
    params.append('base', base);
  }

  const queryString = params.toString();
  const url = `/v0/kucoin/exchange/fiat-price${queryString ? `?${queryString}` : ''}`;

  const response = await authApi.get<FiatPriceResponse>(url);
  return response.data;
};

// ================= Market List ================= //

export const getMarketList = async (): Promise<MarketListResponse> => {
  const response = await authApi.get<MarketListResponse>('/v0/kucoin/exchange/market-list');
  return response.data;
};

// ================= Client IP Address ================= //

export interface ClientIPResponse {
  ip: string;
  commonResponse?: {
    code: string;
    data: {
      ip: string;
    };
    rateLimit: {
      limit: number;
      remaining: number;
      reset: number;
    };
  };
}

export const getClientIPAddress = async (): Promise<ClientIPResponse> => {
  const response = await authApi.get<ClientIPResponse>('/v0/kucoin/exchange/client-ip');
  return response.data;
};

// ================= Server Time ================= //


export const getServerTime = async (): Promise<ServerTimeResponse> => {
  const response = await authApi.get<ServerTimeResponse>('/v0/kucoin/exchange/server-time');
  return response.data;
};

// ================= Currency Details ================= //

export const getCurrency = async (currency: string): Promise<CurrencyDetails> => {
  const params = new URLSearchParams();
  params.append('currency', currency);

  const response = await authApi.get<CurrencyDetails>(`/v0/kucoin/exchange/currency?${params.toString()}`);
  return response.data;
};

// ================= Currency Chain Details ================= //

export const getCurrencyFromTheChain = async (currency: string, chain: string): Promise<CurrencyDetails> => {
  const params = new URLSearchParams();
  params.append('currency', currency);
  params.append('chain', chain);

  const response = await authApi.get<CurrencyDetails>(`/v0/kucoin/exchange/currency/chain?${params.toString()}`);
  return response.data;
};

// ================= All Currencies ================= //

export const getAllCurrencies = async (): Promise<AllCurrenciesResponse> => {
  const response = await authApi.get<AllCurrenciesResponse>('/v0/kucoin/exchange/currencies');
  return response.data;
};

// ================= All Currencies List ================= //

export const getAllCurrenciesList = async (): Promise<string[]> => {
  const response = await authApi.get<string[]>('/v0/kucoin/exchange/currencies/list');
  return response.data;
};

// ================= All Tickers ================= //

export const getAllTickers = async (): Promise<AllTickersResponse> => {
  const response = await authApi.get<AllTickersResponse>('/v0/kucoin/exchange/tickers');
  return response.data;
};

// ================= Symbol Details ================= //


export const getSymbol = async (symbol: string): Promise<SymbolDetails> => {
  const params = new URLSearchParams();
  params.append('symbol', symbol);

  const response = await authApi.get<SymbolDetails>(`/v0/kucoin/exchange/symbol?${params.toString()}`);
  return response.data;
};

// ================= All Symbols ================= //


export const getAllSymbols = async (market?: string): Promise<AllSymbolsResponse> => {
  const params = new URLSearchParams();

  if (market) {
    params.append('market', market);
  }

  const queryString = params.toString();
  const url = `/v0/kucoin/exchange/symbols${queryString ? `?${queryString}` : ''}`;

  const response = await authApi.get<AllSymbolsResponse>(url);
  return response.data;
};
